import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
// Import UI components
import { Button, Popover, Tag, Tooltip } from '@geneui/components';
// Import Components
import AccessControl from 'components/common/AccessControl';
// Import Enums
import { ABTestStatusEnum } from 'enums';
// Import Constants
import { l, PARTNER_SETTINGS } from 'constants/common';
import { ABTestStatusLabels } from 'constants/abTest';

const CampaignABTestInfoModalContent = ({ generalDetails, handleStopABTest }) => {
    const { t } = useTranslation();

    const isVisibleStopABTestButton = useMemo(() => {
        return generalDetails.status === ABTestStatusEnum.InProgress;
    }, [generalDetails]);

    const getPopoverContent = (kpiNames) => (
        <div className="used-in-content">
            <div className="tagList-bc c-tagList-bc">
                {kpiNames.map((title, index) => {
                    return (
                        <Tag
                            key={index}
                            name={title}
                            cornerRadius="smooth-radius"
                            appearance="minimal"
                            flexibility="compact"
                        />
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="campaign-report-ab-test-scrollable-container">
            <div className="crm-campaign-info-detail-modal-body">
                <div className="crm-campaign-info-action-buttons-container">
                    {isVisibleStopABTestButton && (
                        <AccessControl partnerSetting={PARTNER_SETTINGS.IsAllowABTest}>
                            <Tooltip text={t(l.StopABTest)}>
                                <Button
                                    color="primary"
                                    flexibility="default"
                                    appearance="minimal"
                                    icon="bc-icon-test-stop"
                                    onClick={handleStopABTest}
                                />
                            </Tooltip>
                        </AccessControl>
                    )}
                </div>
            </div>
            <div className="segment-modal-head">
                <div className="title-wrapper">
                    <p className="title-wrapper-text">{t(l.GeneralInfo)}</p>
                    <span className="title-wrapper-line" />
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.Status)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">
                        {ABTestStatusLabels[generalDetails.status]}
                    </span>
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.Goal)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{generalDetails.goal}</span>
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.KPIs)}</span>
                    <p className="g-details-subtitle">
                        <Popover
                            className="used-in-popover"
                            extendTargetWidth={false}
                            Content={getPopoverContent(generalDetails.kpiNames)}
                        >
                            <button className="used-in-button">{generalDetails.kpiNames.length}</button>
                        </Popover>
                    </p>
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.StartDate)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{generalDetails.startDate}</span>
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.EndDate)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{generalDetails.endDate}</span>
                </div>
                {generalDetails.status !== ABTestStatusEnum.Failed && (
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.SegmentSplitEndDate)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">
                            {generalDetails.segmentSplitEndDate}
                        </span>
                    </div>
                )}
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.ControlGroup)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{generalDetails.controlGroup}%</span>
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.PilotGroup)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{generalDetails.pilotGroup}%</span>
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.AnalyticsPeriod)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{generalDetails.analyticsPeriod}</span>
                </div>
            </div>
        </div>
    );
};

CampaignABTestInfoModalContent.propTypes = {
    generalDetails: PropTypes.object.isRequired,
    handleStopABTest: PropTypes.func.isRequired,
};

export default CampaignABTestInfoModalContent;
